"use client";

import { useState, useRef, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useAuth } from "@/contexts/auth-context";
import {
  useInstagram,
  InstagramSection,
  ThreadsSection,
  TikTokSection,
  useCachedProfile,
  useCachedTier,
  useCachedSocialConnections,
} from "@/features/account";
import { OAuthErrorBoundary } from "./OAuthErrorBoundary";
import { YouTubeSection } from "@/features/account/components/YouTubeSection";
import { useTranslation } from 'react-i18next';
import type { User } from "@supabase/supabase-js";
import { SectionHeading } from "@/components/ui/SectionHeading";
import { UsageSummaryCard } from "./UsageSummaryCard";
import { GlassPanel } from "@/components/ui/GlassPanel";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { createClient } from "@/auth/client";
import { Eye, EyeOff, LogOut, Globe, ChevronDown } from "lucide-react";
import { SUPPORTED_LANGUAGES, resolveSupportedLanguage } from "@/app/i18n/languages";

interface AccountClientPageProps {
  user: User;
  // userProfile?: any;
  // instagramData?: any;
}

export function AccountClientPage({ user }: AccountClientPageProps) {
  const { t, i18n } = useTranslation();
  const { refreshUser, signOut } = useAuth();
  const currentLanguage = resolveSupportedLanguage(i18n.language);

  const [isPasswordPanelOpen, setIsPasswordPanelOpen] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [repeatNewPassword, setRepeatNewPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showRepeatNewPassword, setShowRepeatNewPassword] = useState(false);

  const [isLangOpen, setIsLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setIsLangOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const hasEmailIdentity = (user.identities ?? []).some((identity) => identity.provider === "email");
  const hasPasswordMetadata = user.user_metadata?.has_password === true;
  const isPasswordSet = hasEmailIdentity || hasPasswordMetadata || Boolean(passwordSuccess);

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isUpdatingPassword) return;

    setPasswordError(null);
    setPasswordSuccess(null);

    if (hasEmailIdentity) {
      if (!user.email) {
        setPasswordError("Your account email is missing. Please sign out and sign in again.");
        return;
      }

      if (!currentPassword) {
        setPasswordError("Please enter your current password.");
        return;
      }
    }

    if (!newPassword || !repeatNewPassword) {
      setPasswordError("Please enter and confirm your new password.");
      return;
    }

    if (newPassword !== repeatNewPassword) {
      setPasswordError("Passwords do not match.");
      return;
    }

    setIsUpdatingPassword(true);

    try {
      const supabase = createClient();

      if (hasEmailIdentity && user.email) {
        const { error: reauthError } = await supabase.auth.signInWithPassword({
          email: user.email,
          password: currentPassword,
        });

        if (reauthError) {
          setPasswordError(reauthError.message);
          return;
        }
      }

      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
        data: { has_password: true }
      });

      if (updateError) {
        setPasswordError(updateError.message);
        return;
      }

      await refreshUser();
      setCurrentPassword("");
      setNewPassword("");
      setRepeatNewPassword("");
      setPasswordSuccess("Password updated successfully.");
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  // Use cached hooks for profile and tier data
  const {
    profile,
    isLoadingProfile,
    profileError,
    usageSummary,
    isLoadingUsageSummary,
    usageSummaryError,
    mutateUsageSummary
  } = useCachedProfile();

  const { tier, isLoadingTier } = useCachedTier();

  // Trigger social connections refresh (e.g., TikTok tokens)
  useCachedSocialConnections();

  // Use Instagram hook for social media functionality
  const instagramHook = useInstagram();

  // Show loading state
  if (isLoadingProfile) {
    return (
      <div className="min-h-screen bg-background pt-16 sm:pt-24">
        <div className="max-w-4xl mx-auto px-2 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-muted rounded w-1/4 mb-8"></div>
            <div className=" border border-neutral-800 rounded-lg shadow-sm p-6">
              <div className="h-6 bg-muted rounded w-1/3 mb-4"></div>
              <div className="h-4 bg-muted rounded w-2/3 mb-4"></div>
              <div className="h-32 bg-muted rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-16 sm:pt-24">
      {/* Handle OAuth errors from URL params with Suspense boundary */}
      <OAuthErrorBoundary />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="px-2 mb-6 sm:mb-8">
          <SectionHeading>{t('account.title')}</SectionHeading>

          {profile?.email && (
            <GlassPanel variant="solid" className="mt-4 p-5 text-white">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2.5 min-w-0 mr-auto">
                  <p className="text-neutral-300 truncate">{profile.email}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <div className="relative" ref={langRef}>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="border-white/10 bg-white/5 text-white hover:bg-white/10 hover:text-white"
                      onClick={() => setIsLangOpen((v) => !v)}
                    >
                      <Globe className="h-3.5 w-3.5 mr-1.5" />
                      {currentLanguage.shortLabel}
                      <ChevronDown className={`h-3 w-3 ml-1 transition-transform ${isLangOpen ? 'rotate-180' : ''}`} />
                    </Button>
                    {isLangOpen && (
                      <div className="absolute top-full left-0 sm:left-auto sm:right-0 mt-1 w-44 max-w-[calc(100vw-2rem)] bg-background border border-border rounded-md shadow-lg z-50 overflow-hidden">
                        {SUPPORTED_LANGUAGES.map((lang) => (
                          <button
                            key={lang.code}
                            onClick={() => {
                              i18n.changeLanguage(lang.code);
                              if (typeof window !== 'undefined' && window.localStorage) {
                                localStorage.setItem('i18nextLng', lang.code);
                              }
                              setIsLangOpen(false);
                            }}
                            className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors ${
                              currentLanguage.code === lang.code
                                ? 'bg-accent text-accent-foreground font-medium'
                                : 'text-foreground hover:bg-accent hover:text-accent-foreground'
                            }`}
                          >
                            {lang.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="border-white/10 bg-white/5 text-white hover:bg-white/10 hover:text-white"
                    onClick={() => {
                      setIsPasswordPanelOpen((v) => !v);
                      setPasswordError(null);
                      setPasswordSuccess(null);
                    }}
                  >
                    {isPasswordSet
                      ? t('account.update_password_button', 'Update Password')
                      : t('account.set_password_button', 'Set Password')
                    }
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="border-white/10 bg-white/5 text-white hover:bg-white/10 hover:text-white"
                    onClick={signOut}
                  >
                    <LogOut className="h-3.5 w-3.5 mr-1.5" />
                    {t('nav.signOut', 'Sign Out')}
                  </Button>
                </div>
              </div>
            </GlassPanel>
          )}
        </div>

        <AnimatePresence>
        {isPasswordPanelOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="mb-6 px-2"
          >
            <GlassPanel variant="solid" className="p-6 text-white">
              <SectionHeading>{t('account.password_panel_title', 'Change / Set Password')}</SectionHeading>
              <p className="mt-2 text-neutral-300">
                {t('account.password_panel_description', 'for your email & password login')}
              </p>

              <form onSubmit={handlePasswordUpdate} className="mt-6 grid gap-4 max-w-md">
                {hasEmailIdentity && (
                  <div className="grid gap-2">
                    <Label htmlFor="current-password">Current password</Label>
                    <div className="relative">
                      <Input
                        id="current-password"
                        type={showCurrentPassword ? "text" : "password"}
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        disabled={isUpdatingPassword}
                        placeholder="********"
                        required
                        className="pr-10"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2 text-neutral-300 hover:bg-white/10 hover:text-white"
                        onClick={() => setShowCurrentPassword((v) => !v)}
                        aria-label={showCurrentPassword ? "Hide password" : "Show password"}
                      >
                        {showCurrentPassword ? <EyeOff /> : <Eye />}
                      </Button>
                    </div>
                  </div>
                )}

                <div className="grid gap-2">
                  <Label htmlFor="new-password">New password</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      disabled={isUpdatingPassword}
                      placeholder="********"
                      required
                      className="pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2 text-neutral-300 hover:bg-white/10 hover:text-white"
                      onClick={() => setShowNewPassword((v) => !v)}
                      aria-label={showNewPassword ? "Hide password" : "Show password"}
                    >
                      {showNewPassword ? <EyeOff /> : <Eye />}
                    </Button>
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="repeat-new-password">Confirm new password</Label>
                  <div className="relative">
                    <Input
                      id="repeat-new-password"
                      type={showRepeatNewPassword ? "text" : "password"}
                      value={repeatNewPassword}
                      onChange={(e) => setRepeatNewPassword(e.target.value)}
                      disabled={isUpdatingPassword}
                      placeholder="********"
                      required
                      className="pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2 text-neutral-300 hover:bg-white/10 hover:text-white"
                      onClick={() => setShowRepeatNewPassword((v) => !v)}
                      aria-label={showRepeatNewPassword ? "Hide password" : "Show password"}
                    >
                      {showRepeatNewPassword ? <EyeOff /> : <Eye />}
                    </Button>
                  </div>
                </div>

                {passwordError && (
                  <Alert variant="destructive">
                    <AlertDescription>{passwordError}</AlertDescription>
                  </Alert>
                )}

                {passwordSuccess && (
                  <Alert className="border-emerald-500/40 bg-emerald-500/10 text-emerald-200">
                    <AlertDescription className="text-emerald-200">
                      {passwordSuccess}
                    </AlertDescription>
                  </Alert>
                )}

                <Button 
                  type="submit" 
                  disabled={isUpdatingPassword}
                  className="text-white"
                >
                  {isUpdatingPassword
                    ? t('account.password_updating', 'Updating...')
                    : hasEmailIdentity
                      ? t('account.password_change_cta', 'Change password')
                      : t('account.password_set_cta', 'Set password')}
                </Button>
              </form>
            </GlassPanel>
          </motion.div>
        )}
        </AnimatePresence>

        <div className="mb-6 px-2">
          <UsageSummaryCard
            summary={usageSummary}
            isLoading={isLoadingUsageSummary}
            error={usageSummaryError?.message ?? null}
            onRetry={mutateUsageSummary}
          />
        </div>

        <div className="px-2 mb-4">
          <SectionHeading>{t('account.connect_social_accounts', 'Connect Your Social Media Accounts')}</SectionHeading>
          <p className="text-sm text-neutral-400 mt-1">
            {t('account.profile_sync_info', 'Profile information syncs automatically when you update AI data')}
          </p>
        </div>

        <div className="space-y-6 px-2">
          <GlassPanel variant="solid" className="p-6 text-white">
            {/* Instagram Section */}
            <InstagramSection
              isLoadingInstagram={instagramHook.isLoadingInstagram}
              isConnecting={instagramHook.isConnecting}
              connection={instagramHook.connection}
              isIndexing={instagramHook.isIndexing}
              currentMessageIndex={instagramHook.currentMessageIndex}
              indexingMessages={instagramHook.indexingMessages}
              onConnectViaFacebook={instagramHook.connectInstagramViaFacebook}
              onConnectDirect={instagramHook.connectInstagramDirect}
              onDisconnect={instagramHook.disconnectInstagram}
              onTriggerIndex={instagramHook.triggerIndex}
              instagramSelectionState={instagramHook.instagramSelectionState}
              onFetchAvailableAccounts={instagramHook.fetchAvailableInstagramAccounts}
              onSelectAccount={instagramHook.selectInstagramAccount}
              onReopenSelection={instagramHook.reopenInstagramSelection}
              onCloseSelection={instagramHook.closeInstagramSelection}
            />
          </GlassPanel>

          <GlassPanel variant="solid" className="p-6 text-white">
            {/* TikTok Section */}
            <TikTokSection />
          </GlassPanel>

          <GlassPanel variant="solid" className="p-6 text-white">
            {/* YouTube Section */}
            <YouTubeSection />
          </GlassPanel>

          {/* <GlassPanel variant="solid" className="p-6 text-white"> */}
            {/* Threads Section */}
            {/* <ThreadsSection /> */}
          {/* </GlassPanel> */}
        </div>
      </div>
    </div>
  );
}
